import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, ChevronRight, Bell, Zap, CloudLightning, Thermometer, Wind } from "lucide-react";
import { Button } from "./ui/button";

const alertIcons = {
  tornado: Zap,
  thunderstorm: CloudLightning,
  heat: Thermometer,
  wind: Wind,
  default: AlertTriangle
};

const severityStyles = {
  warning: {
    bg: "bg-gradient-to-r from-red-500/30 to-orange-500/30",
    border: "border-red-500/50",
    text: "text-red-400",
    glow: "shadow-red-500/20"
  },
  watch: {
    bg: "bg-gradient-to-r from-orange-500/30 to-yellow-500/30",
    border: "border-orange-500/50",
    text: "text-orange-400",
    glow: "shadow-orange-500/20"
  },
  advisory: {
    bg: "bg-gradient-to-r from-yellow-500/30 to-cyan-500/30",
    border: "border-yellow-500/50",
    text: "text-yellow-400",
    glow: "shadow-yellow-500/20"
  }
};

export default function SevereAlertsBanner({ alerts = [] }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    if (alerts.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % alerts.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [alerts.length]);
  
  if (!alerts || alerts.length === 0 || dismissed) return null;
  
  const currentAlert = alerts[currentIndex];
  const severity = currentAlert?.severity || "advisory";
  const style = severityStyles[severity] || severityStyles.advisory;
  
  const getIcon = () => {
    const event = currentAlert?.event?.toLowerCase() || "";
    if (event.includes("tornado")) return alertIcons.tornado;
    if (event.includes("thunder") || event.includes("storm")) return alertIcons.thunderstorm;
    if (event.includes("heat")) return alertIcons.heat;
    if (event.includes("wind")) return alertIcons.wind;
    return alertIcons.default;
  };
  
  const Icon = getIcon();
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`w-full ${style.bg} ${style.border} border rounded-2xl p-4 mb-6 backdrop-blur-md shadow-lg ${style.glow}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <motion.div
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className={`p-2 rounded-xl ${style.bg}`}
            >
              <Icon className={`w-6 h-6 ${style.text}`} />
            </motion.div>
            
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className={`font-bold ${style.text}`}>{currentAlert?.event}</h3>
                {alerts.length > 1 && (
                  <span className="text-xs text-white/40 bg-white/10 px-2 py-0.5 rounded-full">
                    {currentIndex + 1} of {alerts.length}
                  </span>
                )}
              </div>
              
              <p className="text-sm text-white/70 mt-1">
                {currentAlert?.sender || "National Weather Service"}
                {currentAlert?.start && (
                  <> • Until {new Date(currentAlert.start * 1000).toLocaleString()}</>
                )}
              </p>
              {currentAlert?.source_summary && (
                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-black/20 px-3 py-1 text-[10px] text-white/60" data-testid="severe-alert-source-summary">
                  <Bell className="w-3 h-3 text-cyan-300" />
                  {currentAlert.source_summary.agreement} • NWS {currentAlert.source_summary.nws_alert_count || 0} / OWM {currentAlert.source_summary.owm_alert_count || 0}
                </div>
              )}
              
              <AnimatePresence>
                {expanded && currentAlert?.description && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-sm text-white/60 mt-3 leading-relaxed"
                  >
                    {currentAlert.description.slice(0, 300)}...
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {currentAlert?.description && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="text-white/60 hover:text-white"
                data-testid="severe-alert-details-button"
              >
                {expanded ? "Less" : "Details"}
                <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${expanded ? "rotate-90" : ""}`} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDismissed(true)}
              className="rounded-lg hover:bg-white/10 h-8 w-8"
              data-testid="severe-alert-dismiss-button"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Progress dots for multiple alerts */}
        {alerts.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-3">
            {alerts.map((_, idx) => (
              <button
                key={`item-${idx}`}
                onClick={() => setCurrentIndex(idx)}
                data-testid={`severe-alert-dot-${idx}`}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentIndex ? `${style.text.replace('text', 'bg')} scale-125` : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
